/**
 * User management page (Admin only)
 */
import { useState, useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../services/auth'
import { formatLocalDateTime, formatLocalDate, parseServerDate } from '../utils/formatDateTime'
import { qrCodeSvgDataUrl } from '../utils/qrCode'
import {
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  resetPassword,
  resendInvite,
  unlockUser,
  adminListSessions,
  adminDeleteSession,
  adminReauth,
} from '../services/users'
import type { UserSortKey } from '../services/users'
import {
  adminListPasskeys,
  adminDeletePasskey,
  adminEnrollPasskey,
  reauthWithPasskey,
  isPasskeySupported,
} from '../services/passkeys'
import {
  adminListMFADevices,
  adminDeleteMFADevice,
  adminReactivateMFADevice,
  adminEnrollMFADevice,
} from '../services/mfa'
import type { User, UserCreate, UserUpdateAdmin, Passkey, MFADevice, MFAEnrollResponse, UserSession, TrustedDevice, LoginPolicy, RecentLoginIp } from '../types'
import { adminGetLoginPolicy, adminSetLoginPolicy, adminListLoginIps, adminListTrustedDevices, adminRevokeTrustedDevice } from '../services/security'
import { formatCidrLines, parseCidrLines } from '../utils/cidrLines'
import Modal, { ModalFooter } from '../components/Modal'
import { PasswordStrengthBar } from '../components/PasswordStrengthBar'
import { Pagination } from '../components/Pagination'
import { SortableHeader } from '../components/SortableHeader'
import { useTableSort } from '../hooks/useTableSort'

const LIMIT = 25

// Clicking "Last Login" almost always means "who has been here recently", so it opens
// descending. The text and status columns open ascending. Roles has no server sort (it
// is a child table) and Actions is not data, so both stay plain headers.
const DESC_FIRST: readonly UserSortKey[] = ['last_login']
const ALL_ROLES = ['admin', 'employee', 'viewer']

function RoleBadge({ role }: { role: string }) {
  const cls =
    role === 'admin'
      ? 'bg-blue-100 text-blue-800'
      : 'bg-gray-100 text-gray-700'
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${cls} mr-1`}>{role}</span>
  )
}

function StatusBadge({ user }: { user: User }) {
  if (user.locked_until && parseServerDate(user.locked_until)! > new Date()) {
    return (
      <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-800">
        Locked
      </span>
    )
  }
  return (
    <>
      {user.is_active ? (
        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">Active</span>
      ) : (
        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Inactive</span>
      )}
      {user.force_password_change && (
        <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
          PW Reset
        </span>
      )}
      {user.is_active && !user.last_login && (
        <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
          Pending
        </span>
      )}
    </>
  )
}

function isLocked(user: User): boolean {
  return !!(user.locked_until && parseServerDate(user.locked_until)! > new Date())
}

interface CreateModalProps {
  onClose: () => void
  onCreated: () => void
}

function CreateUserModal({ onClose, onCreated }: CreateModalProps) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [roles, setRoles] = useState<string[]>(['viewer'])
  const [sendInvite, setSendInvite] = useState(true)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function toggleRole(role: string) {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (!sendInvite) {
      if (password.length < 15) {
        setError('Password must be at least 15 characters.')
        return
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.')
        return
      }
    }
    if (roles.length === 0) {
      setError('At least one role is required.')
      return
    }

    setSaving(true)
    try {
      const data: UserCreate = {
        email,
        full_name: fullName,
        roles,
        ...(phone ? { phone } : {}),
        // Omit password entirely for the invite flow so the backend emails a
        // set-password link instead.
        ...(sendInvite ? {} : { password }),
      }
      await createUser(data)
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal titleId="create-user-title" title="Create User" onClose={onClose} maxWidth="max-w-md">
      {error && <div className="alert alert-error mb-4" role="alert">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <input
            type="email"
            className="input"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Full Name</label>
          <input
            type="text"
            className="input"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Phone (optional)</label>
          <input
            type="tel"
            className="input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Roles</label>
          <div className="flex gap-4 mt-1">
            {ALL_ROLES.map((role) => (
              <label key={role} className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={roles.includes(role)}
                  onChange={() => toggleRole(role)}
                />
                {role}
              </label>
            ))}
          </div>
        </div>
        <div className="rounded border border-gray-200 bg-gray-50 p-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={sendInvite}
              onChange={(e) => setSendInvite(e.target.checked)}
            />
            <span>Send an invite email so the user sets their own password</span>
          </label>
          <p className="text-xs text-gray-500 mt-1">
            {sendInvite
              ? 'The user receives a set-password link (valid 7 days). Requires SMTP to be configured.'
              : 'You set an initial password below and share it with the user.'}
          </p>
        </div>
        {!sendInvite && (
          <>
            <div>
              <label className="label">Password</label>
              <input
                type="password"
                className="input"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <PasswordStrengthBar password={password} />
              <p className="text-xs text-gray-400 mt-1">
                Minimum 15 characters — uppercase, lowercase, digit, and special character required.
              </p>
            </div>
            <div>
              <label className="label">Confirm Password</label>
              <input
                type="password"
                className="input"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </>
        )}
        <ModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? 'Creating…' : 'Create User'}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

interface ResetPasswordModalProps {
  targetUser: User
  reauthToken: string | null
  onClose: () => void
  onReset: () => void
}

function ResetPasswordModal({ targetUser, reauthToken, onClose, onReset }: ResetPasswordModalProps) {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [forceChange, setForceChange] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (password.length < 15) {
      setError('Password must be at least 15 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setSaving(true)
    try {
      await resetPassword(targetUser.id, password, forceChange, reauthToken)
      onReset()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal titleId="reset-pw-title" title="Reset Password" subtitle={targetUser.email} onClose={onClose} maxWidth="max-w-md">
      {error && <div className="alert alert-error mb-4" role="alert">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">New Password</label>
          <input
            type="password"
            className="input"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordStrengthBar password={password} />
          <p className="text-xs text-gray-400 mt-1">
            Minimum 15 characters — uppercase, lowercase, digit, and special character required.
          </p>
        </div>
        <div>
          <label className="label">Confirm Password</label>
          <input
            type="password"
            className="input"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={forceChange}
              onChange={(e) => setForceChange(e.target.checked)}
            />
            Require user to change password on next login
          </label>
        </div>
        <ModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? 'Resetting…' : 'Reset Password'}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

interface DeleteModalProps {
  targetUser: User
  onClose: () => void
  onDeleted: () => void
}

function DeleteModal({ targetUser, onClose, onDeleted }: DeleteModalProps) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleDelete() {
    setSaving(true)
    try {
      await deleteUser(targetUser.id)
      onDeleted()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user')
      setSaving(false)
    }
  }

  return (
    <Modal titleId="delete-user-title" title="Delete User" onClose={onClose} maxWidth="max-w-sm">
      <p className="text-sm text-gray-600 mb-4">
        Are you sure you want to delete <strong>{targetUser.email}</strong>? This cannot be undone.
      </p>
      {error && <div className="alert alert-error mb-4" role="alert">{error}</div>}
      <ModalFooter>
        <button type="button" onClick={onClose} className="btn btn-secondary">
          Cancel
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={saving}
          className="btn bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
        >
          {saving ? 'Deleting…' : 'Delete'}
        </button>
      </ModalFooter>
    </Modal>
  )
}

interface EditPanelProps {
  targetUser: User
  currentUserId: number
  onClose: () => void
  onSaved: () => void
}

/**
 * The expandable per-user panels, in the order their buttons appear.
 *
 * `label` is the noun; the button shows "Hide <label>" while that panel is open.
 * The wording used to be ad-hoc — Edit toggled to "Close", Passkeys to "Hide Keys",
 * Authenticators to "Hide Auth" — so the same control read three different ways.
 */
type PanelKind = 'edit' | 'sessions' | 'passkeys' | 'mfa' | 'policy'

const PANEL_BUTTONS: { kind: PanelKind; label: string; className: string }[] = [
  { kind: 'edit', label: 'Edit', className: 'text-primary-600 hover:text-primary-800' },
  { kind: 'sessions', label: 'Sessions', className: 'text-orange-600 hover:text-orange-800' },
  { kind: 'passkeys', label: 'Passkeys', className: 'text-indigo-600 hover:text-indigo-800' },
  { kind: 'mfa', label: 'Authenticators', className: 'text-green-600 hover:text-green-800' },
  { kind: 'policy', label: 'Policy', className: 'text-gray-600 hover:text-gray-800' },
]


/**
 * Shared chrome for every expanded row panel (Edit, Sessions, Passkeys,
 * Authenticators, Policy).
 *
 * They used to each roll their own container and Policy had none at all, so it
 * rendered flush against the table while the rest sat in cards. One shell keeps
 * them identical and keeps the width cap in one place: a panel is content, not a
 * table column, so it must not be allowed to widen the table it sits in.
 */
function PanelShell({
  title,
  accent,
  actions,
  onClose,
  children,
}: {
  title: string
  /** Tailwind border colour for the left accent stripe, e.g. "border-indigo-400". */
  accent: string
  actions?: React.ReactNode
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <div className={`card my-2 border-l-4 ${accent} w-full max-w-3xl`}>
      <div className="flex flex-wrap justify-between items-start gap-2 mb-3">
        <h3 className="text-lg font-semibold min-w-0 break-words">{title}</h3>
        <div className="flex gap-2 flex-shrink-0">
          {actions}
          <button className="btn btn-secondary text-sm px-3 py-1" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      {children}
    </div>
  )
}

/**
 * The table row an expanded panel lives in.
 *
 * `.table td` sets `whitespace-nowrap`, which is right for a call sign or a date
 * and catastrophic for a paragraph: the panels' explanatory text becomes one
 * unbreakable line that runs past the card's right edge and is clipped by the
 * table's `overflow-x-auto` wrapper.
 *
 * The wrap MUST go on an element inside the cell, not on the `<td>` itself.
 * Tailwind v3 compiles `@layer` away rather than emitting native cascade layers,
 * so the cascade falls back to specificity — and `.table td` (0-1-1) outranks the
 * `.whitespace-normal` utility (0-1-0). A `whitespace-normal` on the td looks like
 * a fix, computes to `nowrap`, and is silently useless; on this div nothing
 * competes, and the panels inherit `normal` from it.
 */
function PanelRow({ children }: { children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={6} className="px-4 py-0 align-top bg-gray-50">
        <div className="whitespace-normal">{children}</div>
      </td>
    </tr>
  )
}


function EditPanel({ targetUser, currentUserId, onClose, onSaved }: EditPanelProps) {
  const [fullName, setFullName] = useState(targetUser.full_name ?? '')
  const [phone, setPhone] = useState(targetUser.phone ?? '')
  const [isActive, setIsActive] = useState(targetUser.is_active)
  const [forcePasswordChange, setForcePasswordChange] = useState(targetUser.force_password_change)
  const [roles, setRoles] = useState<string[]>(targetUser.roles)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const isSelf = targetUser.id === currentUserId

  function toggleRole(role: string) {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    )
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      const data: UserUpdateAdmin = {
        full_name: fullName,
        phone: phone || undefined,
        is_active: isActive,
        roles,
        force_password_change: forcePasswordChange,
      }
      await updateUser(targetUser.id, data)
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
      setSaving(false)
    }
  }

  return (
    <PanelShell title={`Edit: ${targetUser.email}`} accent="border-primary-500" onClose={onClose}>
      {error && <div className="alert alert-error mb-3">{error}</div>}
      <form onSubmit={handleSave} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label">Full Name</label>
            <input
              type="text"
              className="input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Phone</label>
            <input
              type="tel"
              className="input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={isSelf}
            />
            <span className={isSelf ? 'text-gray-400' : undefined}>Active</span>
            {isSelf && (
              <span className="text-gray-400 text-xs">(cannot deactivate yourself)</span>
            )}
          </label>
          <div className="flex gap-4">
            {ALL_ROLES.map((role) => (
              <label key={role} className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={roles.includes(role)}
                  onChange={() => toggleRole(role)}
                />
                {role}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={forcePasswordChange}
              onChange={(e) => setForcePasswordChange(e.target.checked)}
              disabled={isSelf}
            />
            <span className={isSelf ? 'text-gray-400' : undefined}>
              Require password change on next login
            </span>
            {isSelf && (
              <span className="text-gray-400 text-xs">
                (use Change Password on your Profile instead)
              </span>
            )}
          </label>
        </div>
        <div className="flex gap-3">
          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </form>
    </PanelShell>
  )
}

// ===== Admin Session Panel (inline expand) =====

interface SessionPanelProps {
  targetUser: User
  reauthToken: string | null
  onClose: () => void
}

function SessionPanel({ targetUser, reauthToken, onClose }: SessionPanelProps) {
  const [sessions, setSessions] = useState<UserSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [removingId, setRemovingId] = useState<number | null>(null)
  // Which addresses the saved policy already exempts. Server-computed, so a /24
  // that covers this session counts — an exact-string check would miss that and
  // offer to add a redundant /32.
  const [exemptIps, setExemptIps] = useState<string[]>([])
  const [exemptingIp, setExemptingIp] = useState<string | null>(null)

  useEffect(() => {
    load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true)
    setError('')
    try {
      setSessions(await adminListSessions(targetUser.id))
      await loadExemptIps()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions')
    } finally {
      setLoading(false)
    }
  }

  async function loadExemptIps() {
    const ips = await adminListLoginIps(targetUser.id)
    setExemptIps(ips.filter((i) => i.in_mfa_exempt_cidrs).map((i) => i.ip_address))
  }

  /**
   * Add a session's address to this user's MFA-exempt list.
   *
   * Reads the saved policy and writes it back whole with the new entry appended —
   * the endpoint replaces the policy, so anything not sent is reset to its secure
   * default. Confirmed first because it genuinely weakens this account.
   */
  async function handleExemptIp(ip: string) {
    const cidr = `${ip}/${ip.includes(':') ? '128' : '32'}`
    if (
      !confirm(
        `Let ${targetUser.email} skip the MFA prompt when signing in from ${cidr}?\n\n` +
          `This weakens security: from that address a leaked password alone is enough ` +
          `to sign in as them. Only do this for a network you control.`,
      )
    )
      return
    setExemptingIp(ip)
    setError('')
    setSuccess('')
    try {
      const current = await adminGetLoginPolicy(targetUser.id)
      const next = [...(current.mfa_exempt_cidrs ?? [])]
      if (!next.includes(cidr)) next.push(cidr)
      await adminSetLoginPolicy(targetUser.id, {
        mode: current.mode,
        allowed_cidrs: current.allowed_cidrs,
        mfa_required: current.mfa_required !== false,
        mfa_exempt_cidrs: next,
        // The endpoint replaces the policy, so the existing labels have to be
        // sent back or this quick add would silently erase them.
        cidr_labels: current.cidr_labels ?? {},
      })
      await loadExemptIps()
      setSuccess(`${cidr} added to the MFA-exempt list.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update login policy')
    } finally {
      setExemptingIp(null)
    }
  }

  async function handleRemove(sessionId: number) {
    setRemovingId(sessionId)
    try {
      await adminDeleteSession(targetUser.id, sessionId, reauthToken)
      setSessions((prev) => prev.filter((s) => s.id !== sessionId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove session')
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <PanelShell title={`Sessions: ${targetUser.email}`} accent="border-orange-400" onClose={onClose}>
      {error && <div className="alert alert-error mb-3">{error}</div>}
      {success && <div className="alert alert-success mb-3">{success}</div>}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : sessions.length === 0 ? (
        <p className="text-sm text-gray-500">No active sessions for this user.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {sessions.map((s) => (
            <div key={s.id} className="py-2 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{s.ip_address || 'Unknown IP'}</p>
                <p className="text-xs text-gray-500 truncate">{s.user_agent || 'Unknown browser'}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Started {formatLocalDateTime(s.created_at)} · Last active{' '}
                  {formatLocalDateTime(s.last_activity_at)}
                </p>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2 mt-0.5">
                {s.ip_address &&
                  (exemptIps.includes(s.ip_address) ? (
                    <span className="tap text-xs text-gray-500 px-2 py-1">MFA-exempt</span>
                  ) : (
                    <button
                      className="tap text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-40 px-2 py-1"
                      disabled={exemptingIp === s.ip_address}
                      onClick={() => handleExemptIp(s.ip_address!)}
                      title="Let this user skip the MFA prompt from this address"
                    >
                      {exemptingIp === s.ip_address ? 'Adding…' : '+ MFA-exempt'}
                    </button>
                  ))}
                <button
                  className="tap text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-40 px-2 py-1"
                  disabled={removingId === s.id}
                  onClick={() => handleRemove(s.id)}
                  title="Terminate this session"
                >
                  {removingId === s.id ? 'Removing…' : 'Log out'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </PanelShell>
  )
}

// Renders a QR code locally as an SVG image (no external service, no canvas).
// SVG avoids the qrcode canvas/PNG path, which produced a corrupt raster in prod.
function QRCanvas({ uri }: { uri: string }) {
  const [dataUrl, setDataUrl] = useState('')
  useEffect(() => {
    let active = true
    qrCodeSvgDataUrl(uri).then((u) => { if (active) setDataUrl(u) }).catch(() => {})
    return () => { active = false }
  }, [uri])
  if (!dataUrl) return null
  return <img src={dataUrl} alt="MFA QR code" className="h-48 w-48 border border-gray-200 rounded" />
}

// ===== Admin MFA Panel (inline expand) =====

interface MFAPanelProps {
  targetUser: User
  reauthToken: string | null
  onClose: () => void
}

function MFAPanel({ targetUser, reauthToken, onClose }: MFAPanelProps) {
  const [devices, setDevices] = useState<MFADevice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [removingId, setRemovingId] = useState<number | null>(null)
  // Deactivating is a soft delete, so deactivated devices stay listed (dimmed)
  // and can be restored — otherwise an accidental removal is unrecoverable.
  const [showDeactivated, setShowDeactivated] = useState(false)

  // Add Authenticator flow state
  const [showAddForm, setShowAddForm] = useState(false)
  const [addLabel, setAddLabel] = useState('')
  const [adding, setAdding] = useState(false)
  const [enrollResult, setEnrollResult] = useState<MFAEnrollResponse | null>(null)
  const [codesAcknowledged, setCodesAcknowledged] = useState(false)

  useEffect(() => {
    load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true)
    setError('')
    try {
      // Always ask for the deactivated ones; the toggle only decides what's shown,
      // so flipping it doesn't need another round trip.
      const data = await adminListMFADevices(targetUser.id, true)
      setDevices(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load MFA devices')
    } finally {
      setLoading(false)
    }
  }

  async function handleRemove(deviceId: number, label: string) {
    if (
      !confirm(
        `Deactivate authenticator "${label}" for ${targetUser.email}?\n\n` +
          `They will not be able to sign in with it. You can reactivate it later — ` +
          `the device and its secret are kept.`,
      )
    )
      return
    setRemovingId(deviceId)
    try {
      await adminDeleteMFADevice(targetUser.id, deviceId, reauthToken)
      setDevices((prev) =>
        prev.map((d) => (d.id === deviceId ? { ...d, is_active: false } : d)),
      )
      setShowDeactivated(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate device')
    } finally {
      setRemovingId(null)
    }
  }

  async function handleReactivate(deviceId: number, label: string) {
    if (
      !confirm(
        `Reactivate authenticator "${label}" for ${targetUser.email}?\n\n` +
          `Its secret is unchanged, so this restores the codes it produced before — ` +
          `only do this if the device is still in the right hands.`,
      )
    )
      return
    setRemovingId(deviceId)
    try {
      await adminReactivateMFADevice(targetUser.id, deviceId, reauthToken)
      setDevices((prev) =>
        prev.map((d) => (d.id === deviceId ? { ...d, is_active: true } : d)),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reactivate device')
    } finally {
      setRemovingId(null)
    }
  }

  async function handleAddSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!addLabel.trim()) return
    setAdding(true)
    setError('')
    try {
      const result = await adminEnrollMFADevice(targetUser.id, addLabel.trim(), reauthToken)
      setEnrollResult(result)
      setCodesAcknowledged(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enroll MFA device')
    } finally {
      setAdding(false)
    }
  }

  function handleDoneWithEnrollment() {
    setEnrollResult(null)
    setShowAddForm(false)
    setAddLabel('')
    setCodesAcknowledged(false)
    load()
  }

  const deactivatedCount = devices.filter((d) => d.is_active === false).length
  const visibleDevices = showDeactivated
    ? devices
    : devices.filter((d) => d.is_active !== false)

  function deviceTypeBadge(type: string) {
    if (type === 'backup_codes') {
      return (
        <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800">
          Backup
        </span>
      )
    }
    return (
      <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-800">
        TOTP
      </span>
    )
  }

  return (
    <PanelShell
      title={`Authenticators: ${targetUser.email}`}
      accent="border-green-400"
      onClose={onClose}
      actions={!enrollResult && (
        <button
          className="btn btn-primary text-sm px-3 py-1"
          onClick={() => {
            setShowAddForm((v) => !v)
            setError('')
          }}
        >
          {showAddForm ? 'Cancel' : 'Add Authenticator'}
        </button>
      )}
    >
      {error && <div className="alert alert-error mb-3">{error}</div>}

      {/* Add Authenticator form */}
      {showAddForm && !enrollResult && (
        <form onSubmit={handleAddSubmit} className="mb-4 p-3 bg-green-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">
            Enter a label for this authenticator device (e.g. "iPhone", "YubiKey").
            The user will need to scan the QR code with their authenticator app.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              className="input flex-1"
              placeholder="Device label"
              value={addLabel}
              onChange={(e) => setAddLabel(e.target.value)}
              maxLength={50}
              required
              autoFocus
            />
            <button type="submit" className="btn btn-primary" disabled={adding}>
              {adding ? 'Enrolling…' : 'Enroll'}
            </button>
          </div>
        </form>
      )}

      {/* QR code + backup codes display after enrollment */}
      {enrollResult && (
        <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
          <h4 className="font-semibold text-green-800 mb-2">Authenticator Enrolled</h4>
          <p className="text-sm text-gray-700 mb-3">
            Have the user scan this QR code with their authenticator app (Google Authenticator,
            Authy, 1Password, etc.).
          </p>
          <div className="flex justify-center mb-3">
            <QRCanvas uri={enrollResult.qr_uri} />
          </div>
          <p className="text-xs text-gray-500 text-center mb-3 font-mono break-all">
            Manual entry: {enrollResult.secret}
          </p>

          {!codesAcknowledged && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm font-semibold text-yellow-800 mb-2">
                Save these backup codes — shown once only:
              </p>
              <div className="grid grid-cols-2 gap-1 mb-2">
                {enrollResult.backup_codes.map((code) => (
                  <span key={code} className="font-mono text-sm text-gray-800 bg-white px-2 py-0.5 rounded border">
                    {code}
                  </span>
                ))}
              </div>
              <button
                className="btn btn-primary text-sm mt-2 w-full"
                onClick={() => setCodesAcknowledged(true)}
              >
                I've saved these backup codes
              </button>
            </div>
          )}

          {codesAcknowledged && (
            <button
              className="btn btn-primary text-sm w-full mt-2"
              onClick={handleDoneWithEnrollment}
            >
              Done
            </button>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : visibleDevices.length === 0 ? (
        <p className="text-sm text-gray-500">
          {deactivatedCount > 0
            ? 'No active authenticators for this user.'
            : 'No MFA devices enrolled for this user.'}
        </p>
      ) : (
        <div className="divide-y divide-gray-100">
          {visibleDevices.map((d) => {
            const inactive = d.is_active === false
            return (
              <div
                key={d.id}
                className={`py-2 flex flex-wrap items-center justify-between gap-2 ${
                  inactive ? 'opacity-60' : ''
                }`}
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {d.label}
                    {deviceTypeBadge(d.type)}
                    {d.verified && (
                      <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded bg-green-100 text-green-800">
                        Verified
                      </span>
                    )}
                    {inactive && (
                      <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded bg-gray-200 text-gray-700">
                        Deactivated
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    Added {formatLocalDate(d.created_at)}
                    {d.last_used_at &&
                      ` · Last used ${formatLocalDate(d.last_used_at)}`}
                    {` · Used ${d.use_count} time${d.use_count !== 1 ? 's' : ''}`}
                  </p>
                </div>
                {inactive ? (
                  <button
                    className="tap text-xs text-green-700 hover:text-green-900 font-medium disabled:opacity-40 px-2 py-1"
                    disabled={removingId === d.id}
                    onClick={() => handleReactivate(d.id, d.label)}
                  >
                    {removingId === d.id ? 'Reactivating…' : 'Reactivate'}
                  </button>
                ) : (
                  d.type !== 'backup_codes' && (
                    <button
                      className="tap text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-40 px-2 py-1"
                      disabled={removingId === d.id}
                      onClick={() => handleRemove(d.id, d.label)}
                    >
                      {removingId === d.id ? 'Deactivating…' : 'Deactivate'}
                    </button>
                  )
                )}
              </div>
            )
          })}
        </div>
      )}

      {deactivatedCount > 0 && (
        <button
          className="tap text-xs text-gray-600 hover:text-gray-800 mt-2 px-2 py-1"
          onClick={() => setShowDeactivated((v) => !v)}
        >
          {showDeactivated
            ? 'Hide deactivated'
            : `Show ${deactivatedCount} deactivated authenticator${deactivatedCount === 1 ? '' : 's'}`}
        </button>
      )}
    </PanelShell>
  )
}

// ===== Admin Passkey Panel (inline expand) =====

interface PasskeyPanelProps {
  targetUser: User
  reauthToken: string | null
  onClose: () => void
}

function PasskeyPanel({ targetUser, reauthToken, onClose }: PasskeyPanelProps) {
  const [passkeys, setPasskeys] = useState<Passkey[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [removingId, setRemovingId] = useState<number | null>(null)

  // Add Passkey flow state
  const [showAddForm, setShowAddForm] = useState(false)
  const [addLabel, setAddLabel] = useState('')
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true)
    setError('')
    try {
      const data = await adminListPasskeys(targetUser.id)
      setPasskeys(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load passkeys')
    } finally {
      setLoading(false)
    }
  }

  async function handleRemove(passkeyId: number, label: string) {
    if (!confirm(`Remove passkey "${label}" for ${targetUser.email}?`)) return
    setRemovingId(passkeyId)
    try {
      await adminDeletePasskey(targetUser.id, passkeyId, reauthToken)
      setPasskeys((prev) => prev.filter((pk) => pk.id !== passkeyId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove passkey')
    } finally {
      setRemovingId(null)
    }
  }

  async function handleAddPasskey(e: React.FormEvent) {
    e.preventDefault()
    if (!addLabel.trim()) return
    setAdding(true)
    setError('')
    try {
      const newPasskey = await adminEnrollPasskey(targetUser.id, addLabel.trim(), reauthToken)
      setPasskeys((prev) => [...prev, newPasskey])
      setShowAddForm(false)
      setAddLabel('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enroll passkey')
    } finally {
      setAdding(false)
    }
  }

  return (
    <PanelShell
      title={`Passkeys: ${targetUser.email}`}
      accent="border-indigo-400"
      onClose={onClose}
      actions={(
        <button
          className="btn btn-primary text-sm px-3 py-1"
          onClick={() => {
            setShowAddForm((v) => !v)
            setError('')
          }}
        >
          {showAddForm ? 'Cancel' : 'Add Passkey'}
        </button>
      )}
    >
      {error && <div className="alert alert-error mb-3">{error}</div>}

      {showAddForm && (
        <form onSubmit={handleAddPasskey} className="mb-4 p-3 bg-indigo-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">
            Enter a label, then follow the browser prompt to complete the passkey ceremony.
            The admin's browser will be used but the passkey is saved under {targetUser.email}.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              className="input flex-1 min-w-0"
              placeholder="Passkey label (e.g. YubiKey, MacBook Touch ID)"
              value={addLabel}
              onChange={(e) => setAddLabel(e.target.value)}
              maxLength={50}
              required
              autoFocus
            />
            <button
              type="submit"
              className="btn btn-primary flex-shrink-0"
              disabled={adding}
            >
              {adding ? 'Registering…' : 'Register'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : passkeys.length === 0 ? (
        <p className="text-sm text-gray-500">No passkeys enrolled for this user.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {passkeys.map((pk) => (
            <div key={pk.id} className="py-2 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">{pk.passkey_label}</p>
                <p className="text-xs text-gray-500">
                  Added {formatLocalDate(pk.created_at)}
                  {pk.last_used_at &&
                    ` · Last used ${formatLocalDate(pk.last_used_at)}`}
                  {` · Used ${pk.use_count} time${pk.use_count !== 1 ? 's' : ''}`}
                </p>
              </div>
              <button
                className="tap text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-40"
                disabled={removingId === pk.id}
                onClick={() => handleRemove(pk.id, pk.passkey_label)}
              >
                {removingId === pk.id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          ))}
        </div>
      )}
    </PanelShell>
  )
}

// ===== Admin Re-auth Modal =====

interface ReauthModalProps {
  onVerified: (token: string, expiresAt: number) => void
  onClose: () => void
}

function ReauthModal({ onVerified, onClose }: ReauthModalProps) {
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const result = await adminReauth(password, mfaCode || undefined)
      const expiresAt = Date.now() + result.expires_in_seconds * 1000
      onVerified(result.token, expiresAt)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setSubmitting(false)
    }
  }

  async function handlePasskey() {
    setError('')
    setPasskeyLoading(true)
    try {
      const result = await reauthWithPasskey()
      const expiresAt = Date.now() + result.expires_in_seconds * 1000
      onVerified(result.token, expiresAt)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey verification failed')
    } finally {
      setPasskeyLoading(false)
    }
  }

  return (
    <Modal titleId="admin-reauth-title" title="Confirm it's you" onClose={onClose} maxWidth="max-w-sm">
      <p className="text-sm text-gray-600 mb-4">
        Enter <strong>your own</strong> password and MFA code to authorize this change — this is the
        password for <strong>your admin account</strong>, not a new password for the user you're
        editing. Your verification stays valid for 15 minutes.
      </p>
      {error && <div className="alert alert-error mb-4" role="alert">{error}</div>}
      {isPasskeySupported() && (
        <>
          <button
            type="button"
            onClick={handlePasskey}
            disabled={passkeyLoading || submitting}
            className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {passkeyLoading ? 'Waiting for passkey…' : 'Verify with passkey'}
          </button>
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-2 text-gray-400">or use your password</span>
            </div>
          </div>
        </>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Your password</label>
          <input
            type="password"
            className="input"
            required
            autoFocus
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Your MFA code (if enrolled)</label>
          <input
            type="text"
            className="input"
            inputMode="numeric"
            maxLength={8}
            placeholder="6-digit code or backup code"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
          />
        </div>
        <ModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="btn btn-primary">
            {submitting ? 'Verifying…' : 'Verify'}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

// ===== Admin Login Policy Panel (inline expand) =====

interface LoginPolicyPanelProps {
  targetUser: User
  onClose: () => void
}

function LoginPolicyPanel({ targetUser, onClose }: LoginPolicyPanelProps) {
  const [, setPolicy] = useState<LoginPolicy>({ mode: 'open', allowed_cidrs: [] })
  const [policyMode, setPolicyMode] = useState<'open' | 'ip_only' | 'ip_or_trusted_device'>('open')
  const [policyCidrs, setPolicyCidrs] = useState('')
  // Per-user MFA relaxations — secure defaults (MFA required, no exempt IPs)
  const [mfaRequired, setMfaRequired] = useState(true)
  const [mfaExemptCidrs, setMfaExemptCidrs] = useState('')
  const [trustedDevices, setTrustedDevices] = useState<TrustedDevice[]>([])
  // Addresses this user really signs in from — so the admin isn't guessing.
  const [loginIps, setLoginIps] = useState<RecentLoginIp[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [revoking, setRevoking] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    Promise.all([
      adminGetLoginPolicy(targetUser.id),
      adminListTrustedDevices(targetUser.id),
      adminListLoginIps(targetUser.id),
    ])
      .then(([p, devices, ips]) => {
        setPolicy(p)
        setPolicyMode(p.mode)
        setPolicyCidrs(formatCidrLines(p.allowed_cidrs, p.cidr_labels))
        setMfaRequired(p.mfa_required !== false)
        setMfaExemptCidrs(formatCidrLines(p.mfa_exempt_cidrs ?? [], p.cidr_labels))
        setTrustedDevices(devices)
        setLoginIps(ips)
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false))
  }, [targetUser.id])

  async function handleSave() {
    setSaving(true)
    setError('')
    setSuccess('')
    const allowed = parseCidrLines(policyCidrs)
    const exempt = parseCidrLines(mfaExemptCidrs)
    try {
      const updated = await adminSetLoginPolicy(targetUser.id, {
        mode: policyMode,
        allowed_cidrs: allowed.cidrs,
        mfa_required: mfaRequired,
        mfa_exempt_cidrs: exempt.cidrs,
        cidr_labels: { ...allowed.labels, ...exempt.labels },
      })
      setPolicy(updated)
      setPolicyCidrs(formatCidrLines(updated.allowed_cidrs, updated.cidr_labels))
      setMfaRequired(updated.mfa_required !== false)
      setMfaExemptCidrs(formatCidrLines(updated.mfa_exempt_cidrs ?? [], updated.cidr_labels))
      setSuccess('Policy saved.')
      // Coverage badges are computed server-side against the saved policy.
      adminListLoginIps(targetUser.id).then(setLoginIps).catch(() => {})
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save policy')
    } finally {
      setSaving(false)
    }
  }

  /** Append an address as a single-host CIDR to one of the two lists. */
  function addCidr(
    ip: string,
    value: string,
    setValue: (v: string) => void,
  ) {
    const cidr = `${ip}/${ip.includes(':') ? '128' : '32'}`
    if (parseCidrLines(value).cidrs.includes(cidr)) return
    const lines = value.split('\n').filter((l) => l.trim())
    setValue([...lines, cidr].join('\n'))
    setSuccess('')
  }

  async function handleRevoke(deviceId: number) {
    setRevoking(deviceId)
    try {
      await adminRevokeTrustedDevice(targetUser.id, deviceId)
      setTrustedDevices((prev) => prev.filter((d) => d.id !== deviceId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke device')
    } finally {
      setRevoking(null)
    }
  }

  if (loading) {
    return (
      <PanelShell title={`Login Policy: ${targetUser.email}`} accent="border-gray-400" onClose={onClose}>
        <p className="text-sm text-gray-500">Loading policy…</p>
      </PanelShell>
    )
  }

  return (
    <PanelShell title={`Login Policy: ${targetUser.email}`} accent="border-gray-400" onClose={onClose}>
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      <div className="space-y-3 mb-4">
        <div>
          <label className="label text-xs">Access mode</label>
          <select className="input text-sm" value={policyMode} onChange={(e) => setPolicyMode(e.target.value as typeof policyMode)}>
            <option value="open">Open — no restrictions</option>
            <option value="ip_only">IP only — must come from an allowed IP</option>
            <option value="ip_or_trusted_device">IP or trusted device</option>
          </select>
        </div>
        {policyMode !== 'open' && (
          <div>
            <label className="label text-xs">Allowed IP ranges (CIDR, one per line)</label>
            <textarea
              className="input font-mono text-xs"
              rows={3}
              placeholder={"64.187.71.99/32  # Shop office\n192.168.1.0/24  # Shop LAN"}
              value={policyCidrs}
              onChange={(e) => setPolicyCidrs(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">
              Add <code># a note</code> after an entry to label it. Labels are for
              reading only and never affect who gets in.
            </p>
          </div>
        )}
        {/* ── Per-user MFA relaxations (weaken security for THIS user only) ── */}
        <div className="pt-3 border-t border-gray-200 space-y-3">
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Multi-factor authentication
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              These settings apply to <strong>{targetUser.email}</strong> only and never change the
              site-wide MFA policy. Every change here is recorded in the audit log.
            </p>
          </div>

          <div>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={mfaRequired}
                onChange={(e) => setMfaRequired(e.target.checked)}
              />
              <span>Require MFA at sign-in for this user (recommended)</span>
            </label>
            {!mfaRequired && (
              <div className="alert alert-error mt-2 text-xs">
                <strong>This weakens security.</strong> {targetUser.email} will sign in with a
                password only — no authenticator code, and no first-login MFA setup. Anyone who
                learns or steals that password can sign in as this user. They can still add and use
                a passkey or authenticator app voluntarily, and sensitive actions (changing the
                password, adding or removing a factor) still require any factor they have enrolled.
              </div>
            )}
          </div>

          {mfaRequired && (
            <div>
              <label className="label text-xs">
                MFA-exempt IP ranges (CIDR, one per line) — optional
              </label>
              <textarea
                className="input font-mono text-xs"
                rows={3}
                placeholder={"64.187.69.30/32  # Cheyenne office\n192.168.1.0/24  # Shop LAN"}
                value={mfaExemptCidrs}
                onChange={(e) => setMfaExemptCidrs(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Sign-ins from these networks skip the MFA prompt for this user. Leave empty (the
                default) to always require MFA. This list is separate from the access-mode
                allowlist above. Add <code># a note</code> after an entry to label it.
              </p>
              {mfaExemptCidrs.trim() !== '' && (
                <div className="alert alert-error mt-2 text-xs">
                  <strong>This weakens security.</strong> From these IP addresses a leaked password
                  alone is enough to sign in as {targetUser.email}. Use the narrowest ranges
                  possible (e.g. a single <code>/32</code> office address), and never a range you do
                  not control. IP addresses can be spoofed on untrusted networks. Each sign-in that
                  uses this exemption is audit-logged.
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Where this user actually signs in from ────────────────────────── */}
        <div className="pt-3 border-t border-gray-200">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Recent sign-in addresses
          </h4>
          {loginIps.length === 0 ? (
            <p className="text-xs text-gray-500 mt-1">
              No sign-ins recorded for this user in the last 90 days.
            </p>
          ) : (
            <>
              <p className="text-xs text-gray-500 mt-1 mb-2">
                Addresses this user has signed in from in the last 90 days. An address not listed
                in a policy below is <strong>not</strong> covered by it — a user whose address
                changes will still be asked for an MFA code.
              </p>
              <div className="divide-y divide-gray-200">
                {loginIps.map((entry) => (
                  <div
                    key={entry.ip_address}
                    className="py-2 flex flex-wrap items-center justify-between gap-2"
                  >
                    <div>
                      <p className="text-xs font-mono text-gray-800">{entry.ip_address}</p>
                      <p className="text-xs text-gray-400">
                        {entry.login_count} sign-in{entry.login_count === 1 ? '' : 's'} · last{' '}
                        {formatLocalDate(entry.last_seen)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {policyMode !== 'open' &&
                        (entry.in_allowed_cidrs ? (
                          <span className="text-xs text-gray-500">allowed</span>
                        ) : (
                          <button
                            type="button"
                            className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1"
                            onClick={() => addCidr(entry.ip_address, policyCidrs, setPolicyCidrs)}
                          >
                            + allow
                          </button>
                        ))}
                      {mfaRequired &&
                        (entry.in_mfa_exempt_cidrs ? (
                          <span className="text-xs text-gray-500">MFA-exempt</span>
                        ) : (
                          <button
                            type="button"
                            className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1"
                            onClick={() =>
                              addCidr(entry.ip_address, mfaExemptCidrs, setMfaExemptCidrs)
                            }
                          >
                            + MFA-exempt
                          </button>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Badges reflect the <em>saved</em> policy. Added addresses take effect after Save.
              </p>
            </>
          )}
        </div>

        <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save Policy'}
        </button>
      </div>
      {trustedDevices.length > 0 && (
        <div className="pt-4 border-t border-gray-200">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Trusted Devices</h4>
          <div className="divide-y divide-gray-200">
            {trustedDevices.map((d) => (
              <div key={d.id} className="py-2 flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-medium text-gray-800">{d.device_name}</p>
                  <p className="text-xs text-gray-400">
                    Last seen {formatLocalDate(d.last_seen_at)} · Expires {formatLocalDate(d.expires_at)}
                  </p>
                </div>
                <button
                  className="text-xs text-red-600 hover:text-red-800 disabled:opacity-40"
                  disabled={revoking === d.id}
                  onClick={() => handleRevoke(d.id)}
                >
                  {revoking === d.id ? 'Revoking…' : 'Revoke'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </PanelShell>
  )
}


export default function UsersPage() {
  const { user: currentUser } = useAuth()

  const [users, setUsers] = useState<User[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  // Server-side, so a re-sort reorders every user rather than the 25 on this page. The
  // default (`created_at` descending) is the server's; sending nothing keeps it.
  const { sortKey, sortOrder, handleSort } = useTableSort<UserSortKey>(
    'created_at', 'desc',
    { descFirst: DESC_FIRST, onSortChange: () => setPage(1) },
  )
  const [resendingInviteId, setResendingInviteId] = useState<number | null>(null)

  const [showCreateModal, setShowCreateModal] = useState(false)
  // One expanded panel at a time, across every row. This used to be five separate
  // "which user is this panel open for?" states, each clearing a hand-picked subset
  // of the others — so Sessions replaced Passkeys, but Edit stayed open underneath
  // whatever you opened next. One piece of state makes that impossible.
  const [openPanel, setOpenPanel] = useState<{ userId: number; kind: PanelKind } | null>(null)
  const [resetPasswordFor, setResetPasswordFor] = useState<User | null>(null)
  const [deleteConfirmFor, setDeleteConfirmFor] = useState<User | null>(null)

  // ── Admin re-auth state ──────────────────────────────────────────────────────
  // Token is stored in component memory only (not localStorage), expires in 15 min.
  const [reauthToken, setReauthToken] = useState<string | null>(null)
  const [reauthExpiry, setReauthExpiry] = useState<number>(0)
  const [showReauthModal, setShowReauthModal] = useState(false)
  // Callback to invoke after successful re-auth
  const pendingReauthCallback = useRef<(() => void) | null>(null)

  function isReauthValid(): boolean {
    return reauthToken !== null && Date.now() < reauthExpiry
  }

  const panelIsOpen = (userId: number, kind: PanelKind) =>
    openPanel?.userId === userId && openPanel.kind === kind

  const closePanel = () => setOpenPanel(null)

  /** One row-action button, so all five read and behave the same way. */
  function PanelButton({ user, kind }: { user: User; kind: PanelKind }) {
    const { label, className } = PANEL_BUTTONS.find((b) => b.kind === kind)!
    const open = panelIsOpen(user.id, kind)
    return (
      <button
        className={`tap text-xs font-medium ${className}`}
        aria-expanded={open}
        onClick={() => togglePanel(user, kind)}
      >
        {open ? `Hide ${label}` : label}
      </button>
    )
  }

  /** Toggle a row panel. Opening one closes whatever else was open. */
  function togglePanel(user: User, kind: PanelKind) {
    if (panelIsOpen(user.id, kind)) {
      closePanel()
      return
    }
    // Sessions, passkeys and authenticators expose or revoke credentials, so they
    // sit behind the admin re-auth gate; edit and policy do not.
    const open = () => setOpenPanel({ userId: user.id, kind })
    if (kind === 'edit' || kind === 'policy') open()
    else requireReauth(open)
  }

  /** Compute minutes remaining on the current reauth token (rounded down). */
  function reauthMinutesRemaining(): number {
    if (!isReauthValid()) return 0
    return Math.floor((reauthExpiry - Date.now()) / 60000)
  }

  /**
   * Ensure the admin has a valid re-auth token before proceeding.
   * If valid: invoke callback immediately.
   * If not: open the re-auth modal; the callback fires after successful verification.
   */
  function requireReauth(callback: () => void) {
    if (isReauthValid()) {
      callback()
    } else {
      pendingReauthCallback.current = callback
      setShowReauthModal(true)
    }
  }

  function handleReauthVerified(token: string, expiresAt: number) {
    setReauthToken(token)
    setReauthExpiry(expiresAt)
    setShowReauthModal(false)
    if (pendingReauthCallback.current) {
      pendingReauthCallback.current()
      pendingReauthCallback.current = null
    }
  }

  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!successMessage) return
    successTimerRef.current = setTimeout(() => setSuccessMessage(''), 3000)
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current)
    }
  }, [successMessage])

  useEffect(() => {
    loadUsers(page)
    // loadUsers is re-created every render; listing it would loop. The sort state is
    // listed because changing it must refetch — the server does the sorting, so a
    // client-side reorder of the 25 rows on screen would be a different list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortKey, sortOrder])

  async function loadUsers(p: number) {
    setLoading(true)
    setError('')
    try {
      const res = await listUsers(p, LIMIT, sortKey, sortOrder)
      setUsers(res.users)
      setTotal(res.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  async function handleUnlock(u: User) {
    setError('')
    setSuccessMessage('')
    try {
      await unlockUser(u.id)
      showSuccess(`${u.email} unlocked.`)
      loadUsers(page)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlock user')
    }
  }

  async function handleResendInvite(u: User) {
    setError('')
    setSuccessMessage('')
    setResendingInviteId(u.id)
    try {
      const res = await resendInvite(u.id)
      showSuccess(res.message || `Set-password link sent to ${u.email}.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invite')
    } finally {
      setResendingInviteId(null)
    }
  }


  function showSuccess(msg: string) {
    setSuccessMessage(msg)
  }

  if (!currentUser?.is_superuser) {
    return <Navigate to="/dashboard" replace />
  }

  const totalPages = Math.ceil(total / LIMIT)

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
        {isReauthValid() && (
          <span className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 mt-1">
            Admin verified &#x2713; (expires in {reauthMinutesRemaining()}m)
          </span>
        )}
      </div>

      {error && <div className="alert alert-error mb-4">{error}</div>}
      {successMessage && <div className="alert alert-success mb-4">{successMessage}</div>}

      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Users ({total})</h2>
          <button
            className="btn btn-primary"
            onClick={() => {
              closePanel()
              setShowCreateModal(true)
            }}
          >
            Add User
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <SortableHeader sortKey="email" currentSortKey={sortKey} currentSortOrder={sortOrder} onSort={handleSort}>
                    Email
                  </SortableHeader>
                  <SortableHeader sortKey="full_name" currentSortKey={sortKey} currentSortOrder={sortOrder} onSort={handleSort} className="hidden sm:table-cell">
                    Full Name
                  </SortableHeader>
                  <th>Roles</th>
                  {/* Sorts on `is_active` only. The badge also reflects a lock and a soft
                      delete, so this groups enabled from disabled rather than floating
                      locked accounts to the top — see _USER_SORTS in users.py. */}
                  <SortableHeader sortKey="is_active" currentSortKey={sortKey} currentSortOrder={sortOrder} onSort={handleSort}>
                    Status
                  </SortableHeader>
                  <SortableHeader sortKey="last_login" currentSortKey={sortKey} currentSortOrder={sortOrder} onSort={handleSort} className="hidden md:table-cell">
                    Last Login
                  </SortableHeader>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((u) => (
                  <>
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="text-sm">{u.email}</td>
                      <td className="text-sm hidden sm:table-cell">{u.full_name || '—'}</td>
                      <td>
                        {u.is_superuser ? (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800">
                            superuser
                          </span>
                        ) : (
                          u.roles.map((r) => <RoleBadge key={r} role={r} />)
                        )}
                      </td>
                      <td>
                        <StatusBadge user={u} />
                      </td>
                      <td className="text-sm text-gray-500 hidden md:table-cell">
                        {u.last_login
                          ? formatLocalDateTime(u.last_login)
                          : 'Never'}
                      </td>
                      <td>
                        <div className="flex gap-2 flex-wrap">
                          <PanelButton user={u} kind="edit" />
                          <button
                            className="tap text-xs text-gray-600 hover:text-gray-900 font-medium"
                            onClick={() => requireReauth(() => setResetPasswordFor(u))}
                          >
                            Reset PW
                          </button>
                          {!u.last_login && (
                            <button
                              className="tap text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                              disabled={resendingInviteId === u.id}
                              onClick={() => handleResendInvite(u)}
                              title="Email this user a set-password link"
                            >
                              {resendingInviteId === u.id ? 'Sending…' : 'Resend invite'}
                            </button>
                          )}
                          {isLocked(u) && (
                            <button
                              className="tap text-xs text-orange-600 hover:text-orange-800 font-medium"
                              onClick={() => handleUnlock(u)}
                            >
                              Unlock
                            </button>
                          )}
                          <button
                            className="tap text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                            disabled={u.id === currentUser.id}
                            onClick={() => setDeleteConfirmFor(u)}
                            title={u.id === currentUser.id ? "Can't delete your own account" : ''}
                          >
                            Delete
                          </button>
                          <PanelButton user={u} kind="sessions" />
                          <PanelButton user={u} kind="passkeys" />
                          <PanelButton user={u} kind="mfa" />
                          <PanelButton user={u} kind="policy" />
                        </div>
                      </td>
                    </tr>
                    {panelIsOpen(u.id, 'edit') && (
                      <PanelRow key={`edit-${u.id}`}>
                        <EditPanel
                          targetUser={u}
                          currentUserId={currentUser!.id}
                          onClose={closePanel}
                          onSaved={() => {
                            closePanel()
                            showSuccess(`${u.email} updated.`)
                            loadUsers(page)
                          }}
                        />
                      </PanelRow>
                    )}
                    {panelIsOpen(u.id, 'sessions') && (
                      <PanelRow key={`sessions-${u.id}`}>
                        <SessionPanel targetUser={u} reauthToken={reauthToken} onClose={closePanel} />
                      </PanelRow>
                    )}
                    {panelIsOpen(u.id, 'passkeys') && (
                      <PanelRow key={`passkeys-${u.id}`}>
                        <PasskeyPanel targetUser={u} reauthToken={reauthToken} onClose={closePanel} />
                      </PanelRow>
                    )}
                    {panelIsOpen(u.id, 'mfa') && (
                      <PanelRow key={`mfa-${u.id}`}>
                        <MFAPanel targetUser={u} reauthToken={reauthToken} onClose={closePanel} />
                      </PanelRow>
                    )}
                    {panelIsOpen(u.id, 'policy') && (
                      <PanelRow key={`policy-${u.id}`}>
                        <LoginPolicyPanel targetUser={u} onClose={closePanel} />
                      </PanelRow>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false)
            showSuccess('User created successfully.')
            loadUsers(page)
          }}
        />
      )}

      {resetPasswordFor && (
        <ResetPasswordModal
          targetUser={resetPasswordFor}
          reauthToken={reauthToken}
          onClose={() => setResetPasswordFor(null)}
          onReset={() => {
            setResetPasswordFor(null)
            showSuccess('Password reset successfully.')
          }}
        />
      )}

      {deleteConfirmFor && (
        <DeleteModal
          targetUser={deleteConfirmFor}
          onClose={() => setDeleteConfirmFor(null)}
          onDeleted={() => {
            setDeleteConfirmFor(null)
            showSuccess('User deleted.')
            loadUsers(page)
          }}
        />
      )}

      {showReauthModal && (
        <ReauthModal
          onVerified={handleReauthVerified}
          onClose={() => {
            setShowReauthModal(false)
            pendingReauthCallback.current = null
          }}
        />
      )}
    </div>
  )
}
